import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { SidebarProvider } from './contexts/SidebarContext';
import { TutorialProvider } from './contexts/TutorialContext';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import SignUp from './components/SignUp';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import MaterialManagement from './components/MaterialManagement';
import LabPage from './components/LabPage';
import SubscriptionManagement from './components/SubscriptionManagement';
import PublicAccessManagement from './components/PublicAccessManagement';
import HomePageManagement from './components/HomePageManagement';
import BookingManagement from './components/BookingManagement';
import CoursesManagement from './components/CoursesManagement';
import UserManagement from './components/UserManagement';
import AssessmentPage from './components/AssessmentPage';
import StructuralSegmentPractice from './components/StructuralSegmentPractice';
import StructuredSegmentsList from './components/StructuredSegmentsList';
import DialogueTopicsList from './components/DialogueTopicsList';
import DialogueTopicDetail from './components/DialogueTopicDetail';
import PodcastPage from './components/PodcastPage';
import PodcastPlayer from './components/PodcastPlayer';
import TestRules from './pages/TestRules';
import MockService from './pages/MockService';
import MockResultPage from './pages/MockResultPage';
import MockGradingPage from './pages/MockGradingPage';
import MockCreditManagement from './components/MockCreditManagement';
import SubscriptionPurchaseManagement from './components/SubscriptionPurchaseManagement';
import SupportAIManagement from './components/SupportAIManagement';
import SupportChatWidget from './components/SupportChatWidget';
import OnlineCourses from './pages/OnlineCourses';
import Booking from './pages/Booking';
import SubscriptionPurchase from './pages/SubscriptionPurchase';
import { ThemeProvider } from './contexts/ThemeContext';
import { authAPI, UserProfile, LoginData } from './services/api';
import { isAuthenticated as checkAuth, removeToken } from './config/api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthentication = async () => {
      if (checkAuth()) {
        try {
          const profile = await authAPI.getProfile();
          setUserProfile(profile);
          setUserEmail(profile.email || profile.username || '');
          setIsAuthenticated(true);
          setIsAdmin(profile.is_staff || profile.is_superuser || false);
        } catch (error) {
          console.error('Failed to get user profile:', error);
          removeToken();
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      }
      setIsLoading(false);
    };

    checkAuthentication();
  }, []);

  const handleLogin = async (
    identifier: string,
    password: string,
    allowUsernameSignup: boolean
  ) => {
    try {
      const trimmed = identifier.trim();
      const payload: LoginData = { password };
      if (allowUsernameSignup && !trimmed.includes('@')) {
        payload.username = trimmed;
      } else {
        payload.email = trimmed;
      }
      const response = await authAPI.login(payload);
      setUserProfile(response.user);
      setUserEmail(response.user.email || response.user.username || '');
      setIsAuthenticated(true);
      setIsAdmin(response.user.is_staff || response.user.is_superuser || false);
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const handleSignUp = async (
    account: string,
    password: string,
    passwordConfirm: string,
    organization?: string,
    firstName?: string,
    lastName?: string,
    mode: 'email' | 'username' = 'email'
  ) => {
    try {
      const response =
        mode === 'username'
          ? await authAPI.registerWithUsername({
              username: account.trim(),
              password,
              password_confirm: passwordConfirm,
              organization: organization || '',
              first_name: firstName || '',
              last_name: lastName || '',
            })
          : await authAPI.register({
              email: account.trim(),
              password,
              password_confirm: passwordConfirm,
              organization: organization || '',
              first_name: firstName || '',
              last_name: lastName || '',
            });
      setUserProfile(response.user);
      setUserEmail(response.user.email || response.user.username || '');
      setIsAuthenticated(true);
      setIsAdmin(response.user.is_staff || response.user.is_superuser || false);
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUserEmail('');
      setUserProfile(null);
      setIsAdmin(false);
    }
  };

  return (
    <ThemeProvider>
      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'var(--bg-app)',
          color: 'var(--text-primary)'
        }}>
          Loading...
        </div>
      ) : (
    <Router>
      <SidebarProvider>
        <TutorialProvider>
        <Routes>
          {/* Landing Page - Public */}
          <Route
            path="/"
            element={<LandingPage isAuthenticated={isAuthenticated} userProfile={userProfile} />}
          />
          
          {/* Login - Public */}
          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <Login 
                  onLogin={handleLogin}
                />
              ) : (
                isAdmin ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              )
            }
          />
          
          {/* Sign Up - Public */}
          <Route
            path="/signup"
            element={
              !isAuthenticated ? (
                <SignUp 
                  onSignUp={handleSignUp} 
                />
              ) : (
                isAdmin ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              )
            }
          />
          
          {/* Dashboard - Protected */}
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                isAdmin ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : (
                  <Dashboard onLogout={handleLogout} userEmail={userEmail} />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          
          {/* Admin Dashboard - Protected */}
          <Route
            path="/admin/dashboard"
            element={
              isAuthenticated && isAdmin ? (
                <AdminDashboard onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/landing-page"
            element={
              isAuthenticated && isAdmin ? (
                <HomePageManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Material Management - Admin Only */}
          <Route
            path="/admin/materials"
            element={
              isAuthenticated && isAdmin ? (
                <MaterialManagement onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Lab - Admin Only */}
          <Route
            path="/admin/lab"
            element={
              isAuthenticated && isAdmin ? (
                <LabPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Subscription Management - Admin Only */}
          <Route
            path="/admin/subscriptions"
            element={
              isAuthenticated && isAdmin ? (
                <SubscriptionManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Public access (free without subscription) — Admin Only */}
          <Route
            path="/admin/public-access"
            element={
              isAuthenticated && isAdmin ? (
                <PublicAccessManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Booking Management - Admin Only */}
          <Route
            path="/admin/booking"
            element={
              isAuthenticated && isAdmin ? (
                <BookingManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/courses"
            element={
              isAuthenticated && isAdmin ? (
                <CoursesManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* User Management - Admin Only */}
          <Route
            path="/admin/users"
            element={
              isAuthenticated && isAdmin ? (
                <UserManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Mock Grading - Admin Only */}
          <Route
            path="/admin/mock-credits"
            element={
              isAuthenticated && isAdmin ? (
                <MockCreditManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/subscription-requests"
            element={
              isAuthenticated && isAdmin ? (
                <SubscriptionPurchaseManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/naati-ai"
            element={
              isAuthenticated && isAdmin ? (
                <SupportAIManagement onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/mock-grading"
            element={
              isAuthenticated && isAdmin ? (
                <MockGradingPage onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/mock-grading/:submissionId"
            element={
              isAuthenticated && isAdmin ? (
                <MockGradingPage onLogout={handleLogout} userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        <Route
          path="/test-rules"
          element={
            isAuthenticated ? (
              <TestRules onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/structured-segments"
          element={
            isAuthenticated ? (
              <StructuredSegmentsList onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/structured-segments/:segmentId"
          element={
            isAuthenticated ? (
              <StructuralSegmentPractice onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/podcast"
          element={
            isAuthenticated ? (
              <PodcastPage onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/podcast/:podcastId"
          element={
            isAuthenticated ? (
              <PodcastPlayer onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/:type"
          element={
            isAuthenticated ? (
              <AssessmentPage onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/dialogue-categories"
          element={
            isAuthenticated ? (
              <DialogueTopicsList onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/dialogues/:dialogueId/materials"
          element={
            isAuthenticated ? (
              <DialogueTopicDetail onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/material/dialogues/:dialogueId"
          element={
            isAuthenticated ? (
              <AssessmentPage onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/mock-service"
          element={
            isAuthenticated ? (
              <MockService onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/mock-service/result/:submissionId"
          element={
            isAuthenticated ? (
              <MockResultPage onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/online-courses/:type?"
          element={
            isAuthenticated ? (
              <OnlineCourses onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/booking"
          element={
            isAuthenticated ? (
              <Booking onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/subscription/purchase"
          element={
            isAuthenticated ? (
              <SubscriptionPurchase onLogout={handleLogout} userEmail={userEmail} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/assessment"
          element={
            isAuthenticated ? (
              <AssessmentPage onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />} />
        </Routes>
        <SupportChatWidget isAuthenticated={isAuthenticated} />
        </TutorialProvider>
      </SidebarProvider>
    </Router>
      )}
    </ThemeProvider>
  );
}

export default App;
